// Redux Slice for Frictionless Signup State Management
// Handles signup flow state, API calls, and user journey progress

import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import posthog from 'posthog-js';
import {
  signupApi,
  type WebsiteSignupRequest,
  getErrorMessage,
  parseFieldValidationErrors,
  type FieldValidationErrors,
} from '../services/signupApi';
import type { JourneyConfig } from '../config/journeyConfig';
import { setJourneyContext as setOnboardingJourneyContext } from 'features/onboarding/reducers/onboardingSlice';

/**
 * Account progress data from backend when existing account is detected
 */
interface AccountProgress {
  email: string;
  current_status: string;
  progress_percentage: number;
  friendly_milestone: string;
  has_snowflake_account: boolean;
  has_runner: boolean;
  created_at: string;
  app_name?: string | null;
  app_variant?: string | null;
}

/**
 * Signup state interface
 * Tracks the current state of the signup journey
 */
interface SignupState {
  signupId: number | null;
  currentStep: 'signup' | 'password_setup' | 'snowflake_setup' | 'runner_provision' | 'complete';
  isLoading: boolean;
  error: string | null;
  fieldErrors: FieldValidationErrors | null;
  signupSuccess: boolean;
  runnerProvisioned: boolean;
  journeyConfig: JourneyConfig | null;
  accountFound: boolean;
  accountProgress: AccountProgress | null;
  /**
   * Whether new website signups are currently being accepted.
   *
   * - `null`  : not yet checked (initial state, before the availability
   *             probe completes).  Used by `SignupView` to render a
   *             loading state instead of flashing the form.
   * - `true`  : signups are accepted, render the form.
   * - `false` : signups are disabled, render the `SignupDisabled` screen.
   *
   * Populated by `fetchSignupAvailability` and also flipped to `false` if
   * `submitSignup` rejects with a 400 whose message contains "disabled"
   * (handles the race where ops toggles the flag between page load and
   * submit).
   */
  signupAvailable: boolean | null;
}

/**
 * Initial state for signup
 */
const initialState: SignupState = {
  signupId: null,
  currentStep: 'signup',
  isLoading: false,
  error: null,
  fieldErrors: null,
  signupSuccess: false,
  runnerProvisioned: false,
  journeyConfig: null,
  accountFound: false,
  accountProgress: null,
  signupAvailable: null,
};

/**
 * Async thunk to submit signup form
 *
 * TODO: This will fail until backend API is implemented
 * When backend is ready:
 * 1. Remove try-catch mock error handling
 * 2. Backend should return signup_id and status
 * 3. Backend handles ReCaptcha validation
 * 4. Backend creates Auth0 user and sends email
 */
export const submitSignup = createAsyncThunk(
  'signup/submitSignup',
  async (data: WebsiteSignupRequest, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await signupApi.signup(data);

      // Copy journey config to onboarding store for continuity
      const state = getState() as { signup: SignupState };
      if (state.signup.journeyConfig !== null) {
        dispatch(
          setOnboardingJourneyContext({
            app_name: state.signup.journeyConfig.app_name,
            app_variant: state.signup.journeyConfig.app_variant,
            redirect_to: state.signup.journeyConfig.redirect_to,
          }),
        );
      }

      // Identify user with their email to create person profile
      posthog.identify(data.email);

      // Track successful signup completion
      posthog.capture('admin_app.frictionless.signup_completed', {
        app_name: state.signup.journeyConfig?.app_name ?? 'DataOps.live',
        app_variant: state.signup.journeyConfig?.app_variant ?? 'default',
      });

      return response;
    } catch (error: unknown) {
      // Check if this is a 409 Conflict (existing account)
      if (typeof error === 'object' && error !== null) {
        const err = error as any;
        if (err.response?.status === 409 && err.response?.data?.error_type === 'EXISTING_ACCOUNT') {
          // Existing account found - return progress data for Account Found page
          return rejectWithValue({
            message: err.response.data.message,
            fieldErrors: null,
            accountFound: true,
            accountProgress: err.response.data.data,
          });
        }

        // Detect a 400 from the feature-flag gate.  This handles the race
        // where the availability probe returned `enabled = true` on page
        // load but ops toggled the flag off before the user submitted.
        // We surface this as `signupDisabled` so the reducer can swap to
        // the `SignupDisabled` screen instead of showing a generic error.
        if (err.response?.status === 400) {
          const detail: unknown = err.response?.data?.detail;
          if (typeof detail === 'string' && detail.toLowerCase().includes('disabled')) {
            return rejectWithValue({
              message: detail,
              fieldErrors: null,
              accountFound: false,
              accountProgress: null,
              signupDisabled: true,
            });
          }
        }
      }

      // Try to parse field-level validation errors
      const fieldErrors = parseFieldValidationErrors(error);

      if (fieldErrors != null) {
        // Return both field errors and a general message
        return rejectWithValue({
          message: 'Please correct the errors in the form',
          fieldErrors,
          accountFound: false,
          accountProgress: null,
        });
      }

      // Fall back to general error message
      const message = getErrorMessage(error);
      return rejectWithValue({
        message,
        fieldErrors: null,
        accountFound: false,
        accountProgress: null,
      });
    }
  },
);

/**
 * Async thunk to provision runner
 *
 * The backend endpoint uses the authenticated user's context to determine
 * which signup to provision a runner for. No parameters needed.
 */
export const provisionRunner = createAsyncThunk('signup/provisionRunner', async (_, { rejectWithValue }) => {
  try {
    const response = await signupApi.provisionRunner();
    return response;
  } catch (error: unknown) {
    const message = getErrorMessage(error);
    return rejectWithValue(message);
  }
});

/**
 * Async thunk to fetch current user's signup status
 *
 * Gets the signup status for the currently authenticated user.
 * Returns null if no signup exists for this user.
 */
export const fetchSignupStatus = createAsyncThunk('signup/fetchStatus', async (_, { rejectWithValue }) => {
  try {
    const response = await signupApi.getCurrentUserSignupStatus();
    return response;
  } catch (error: unknown) {
    const message = getErrorMessage(error);
    return rejectWithValue(message);
  }
});

/**
 * Async thunk to check whether website signups are currently being accepted.
 *
 * Called by `SignupView` on mount so the form can render a "signups
 * disabled" screen instead of the form when the backend's PostHog flag
 * is off.  If the probe itself fails (network error, etc.) we fall back
 * to `enabled = true` so the user can still attempt the flow - the
 * existing 400 error path on submit will handle a real disabled backend.
 * Returning `true` on error is deliberately NOT fail-closed because the
 * backend is the actual gate; this probe is purely a UX optimisation.
 */
export const fetchSignupAvailability = createAsyncThunk('signup/fetchAvailability', async (): Promise<boolean> => {
  try {
    const response = await signupApi.getSignupAvailability();
    return response.enabled;
  } catch {
    // If the probe fails, optimistically allow the form to render.
    // The POST /signup endpoint is the authoritative gate.
    return true;
  }
});

/**
 * Signup slice
 * Manages signup state, journey configuration, and flow progression
 */
const signupSlice = createSlice({
  name: 'signup',
  initialState,
  reducers: {
    /**
     * Set the current step in the signup journey
     */
    setCurrentStep: (state, action: PayloadAction<SignupState['currentStep']>) => {
      state.currentStep = action.payload;
    },

    /**
     * Set the journey configuration from URL params
     */
    setJourneyConfig: (state, action: PayloadAction<JourneyConfig>) => {
      state.journeyConfig = action.payload;
    },

    /**
     * Reset signup state (e.g., when starting a new signup)
     */
    resetSignup: (state) => {
      Object.assign(state, initialState);
    },

    /**
     * Clear error message
     */
    clearError: (state) => {
      state.error = null;
      state.fieldErrors = null;
    },
  },
  extraReducers: (builder) => {
    // Submit signup reducers
    builder
      .addCase(submitSignup.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.fieldErrors = null;
        state.accountFound = false;
        state.accountProgress = null;
      })
      .addCase(submitSignup.fulfilled, (state, action) => {
        state.isLoading = false;

        // Check for existing_account_created_signup status (edge case)
        if (
          action.payload.status === 'existing_account_created_signup' &&
          typeof action.payload.data !== 'undefined' &&
          action.payload.data !== null
        ) {
          state.accountFound = true;
          state.accountProgress = action.payload.data;
          state.signupId = action.payload.signup_id ?? null;
        } else {
          // Normal successful signup
          state.signupSuccess = true;
          state.signupId = action.payload.signup_id ?? null;
          state.currentStep = 'password_setup';
        }
      })
      .addCase(submitSignup.rejected, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as
          | {
              message: string;
              fieldErrors: FieldValidationErrors | null;
              accountFound?: boolean;
              accountProgress?: AccountProgress | null;
              signupDisabled?: boolean;
            }
          | string;

        if (typeof payload === 'object' && payload !== null) {
          state.error = payload.message;
          state.fieldErrors = payload.fieldErrors;

          // Handle existing account case (409 response)
          if (
            payload.accountFound === true &&
            payload.accountProgress !== null &&
            payload.accountProgress !== undefined
          ) {
            state.accountFound = true;
            state.accountProgress = payload.accountProgress;
            state.error = null; // Clear error since this is an expected scenario
          }

          // Handle 400 "feature disabled" race - flag was toggled off
          // between page load and submit.  Swap the UI to the disabled
          // screen instead of showing a generic error banner.
          if (payload.signupDisabled === true) {
            state.signupAvailable = false;
            state.error = null;
          }
        } else {
          state.error = payload ?? 'Signup failed';
          state.fieldErrors = null;
        }
      });

    // Provision runner reducers
    builder
      .addCase(provisionRunner.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(provisionRunner.fulfilled, (state) => {
        state.isLoading = false;
        state.runnerProvisioned = true;
        state.currentStep = 'complete';
      })
      .addCase(provisionRunner.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) ?? 'Runner provisioning failed';
      });

    // Fetch signup status reducers
    builder
      .addCase(fetchSignupStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSignupStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        // If no signup exists, action.payload will be null
        if (action.payload !== null) {
          // Map the SignupStatus to the currentStep
          // This is a simplified mapping - adjust based on your needs
          if (action.payload.has_runner) {
            state.runnerProvisioned = true;
          }
        }
      })
      .addCase(fetchSignupStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) ?? 'Failed to fetch signup status';
      });

    // Fetch signup availability reducers.
    //
    // We deliberately do NOT touch `isLoading` / `error` here: the global
    // `isLoading` is reserved for the submit flow's spinner state, and
    // `SignupView` reads `signupAvailable === null` directly to decide
    // whether to show its own loading state.  The thunk never rejects
    // (network errors are swallowed and resolved as `true`) so a
    // `.rejected` reducer would be dead code.
    builder.addCase(fetchSignupAvailability.fulfilled, (state, action) => {
      state.signupAvailable = action.payload;
    });
  },
});

// Export actions
export const { setCurrentStep, setJourneyConfig, resetSignup, clearError } = signupSlice.actions;

// Export reducer
export default signupSlice.reducer;

/**
 * Selectors for accessing signup state
 *
 * Usage in components:
 * ```typescript
 * const { isLoading, error, signupSuccess } = useSelector((state: RootState) => state.signup);
 * ```
 */
export const selectSignup = (state: { signup: SignupState }): SignupState => state.signup;
export const selectIsLoading = (state: { signup: SignupState }): boolean => state.signup.isLoading;
export const selectError = (state: { signup: SignupState }): string | null => state.signup.error;
export const selectSignupSuccess = (state: { signup: SignupState }): boolean => state.signup.signupSuccess;
export const selectCurrentStep = (state: { signup: SignupState }): SignupState['currentStep'] =>
  state.signup.currentStep;
export const selectJourneyConfig = (state: { signup: SignupState }): JourneyConfig | null => state.signup.journeyConfig;
export const selectAccountFound = (state: { signup: SignupState }): boolean => state.signup.accountFound;
export const selectAccountProgress = (state: { signup: SignupState }): AccountProgress | null =>
  state.signup.accountProgress;
export const selectSignupAvailable = (state: { signup: SignupState }): boolean | null => state.signup.signupAvailable;
